import { ServerSideMetricsDetailByTime } from '@pubstack/common/src/analytics/query'
import { CurrencySymbol } from '@pubstack/common/src/currency'
import { Timezone } from '@pubstack/common/src/timezone'
import { DateTime } from 'luxon'
import { FunctionComponent } from 'react'
import { ChartWrapperOptions } from 'react-google-charts'

import { WidgetProps } from '~/components/Widget'
import { AnalyticsDefaultChartOptions } from '~/modules/analytics/AnalyticsCharts'
import { AnalyticsChartWidget } from '~/modules/analytics/AnalyticsChartWidget'
import { TimelineConfiguration, useTimelineChart } from '~/modules/analytics/analyticsTimeline.hooks'
import { serverSideBidRate, serverSideBidRequests, serverSideBidTimeoutRate, serverSideBidTimeouts, serverSideNoBidRate } from '~/modules/analytics/common/formulas'
import { WithClassName } from '~/types/utils'
import { ANALYTICS_TOOLTIPS } from '~/utils/constants'

const useConf = () => {
  const chartOptions: ChartWrapperOptions['options'] = {
    ...AnalyticsDefaultChartOptions,
    annotations: { ...AnalyticsDefaultChartOptions.annotations, style: 'point' },
    crosshair: { ...AnalyticsDefaultChartOptions.crosshair, trigger: 'both' },
    vAxis: {
      ...AnalyticsDefaultChartOptions.vAxis,
      gridlines: { count: -1 },
      minorGridlines: { color: 'none' },
      format: 'percent',
      minValue: 0,
    },
    isStacked: 'relative',
  }
  return {
    getChartOptions: () => chartOptions,
    legendConfig: ['bidTimeoutRate', 'bidRate', 'noBidRate'],
    tooltipConfig: ['noBidRate', 'bidRate', 'bidTimeoutRate'],
    dataConfig: [
      {
        name: 'bidTimeoutRate',
        isComputable: ({ data }) => serverSideBidTimeoutRate.isComputable(data),
        getFormula: () => serverSideBidTimeoutRate,
        legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_TIMEOUT_RATE,
        withValue: true,
      },
      {
        name: 'bidRate',
        isComputable: ({ data }) => serverSideBidRate.isComputable(data),
        getFormula: () => serverSideBidRate,
        legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_BID_RATE,
        withValue: true,
      },
      {
        name: 'noBidRate',
        isComputable: ({ data }) => serverSideNoBidRate.isComputable(data),
        getFormula: () => serverSideNoBidRate,
        legendTooltip: ANALYTICS_TOOLTIPS.SERVER_SIDE_BIDDER_DETAILS_NO_BID_RATE,
        withValue: true,
      },
    ],
  } as TimelineConfiguration<ServerSideMetricsDetailByTime>
}

type ServerSideWidgetResponsesProps = WithClassName &
  Omit<WidgetProps, 'title' | 'icon' | 'info' | 'refreshTrackingLabel'> & {
    data: ServerSideMetricsDetailByTime
    timezone: Timezone
    currentEpoch: DateTime
    currencySymbol: CurrencySymbol
  }
export const ServerSideWidgetResponses: FunctionComponent<ServerSideWidgetResponsesProps> = ({ data, currencySymbol, currentEpoch, timezone, ...props }) => {
  const allComputable = data && serverSideBidRequests.isComputable(data) && serverSideBidTimeouts.isComputable(data)

  const currentConfig = useConf()
  const { chart, legends } = useTimelineChart({ currentConfig, data, currencySymbol, currentEpoch, timezone, chartType: 'AreaChart' })

  return (
    <AnalyticsChartWidget
      {...props}
      empty={props.empty || (!props.isLoading && !allComputable)}
      icon={'bidder'}
      title={'Bidder responses'}
      smallChart={true}
      chart={chart}
      legends={legends.reverse()}
      refreshTrackingLabel={'ana-bidders-detail-responses-timeline-refresh'}
    />
  )
}
